import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";

// Dashboard
export function useDashboardStats() {
  return useQuery({
    queryKey: ["dashboard", "stats"],
    queryFn: () =>
      api.get<{
        totalActive: number;
        totalCancelled: number;
        totalSuperseded: number;
        createdThisMonth: number;
        pdfsThisMonth: number;
        expiringSoonCount: number;
      }>("/api/dashboard/stats"),
  });
}

export function useDashboardRecent(limit = 15) {
  return useQuery({
    queryKey: ["dashboard", "recent", limit],
    queryFn: () =>
      api.get<{
        records: {
          id: number;
          address: string;
          insured: string;
          valuer: string;
          status: string;
          pocTo: string | null;
          updatedAt: string;
        }[];
      }>(`/api/dashboard/recent?limit=${limit}`),
  });
}

export function useRenewalForecast() {
  return useQuery({
    queryKey: ["dashboard", "renewal-forecast"],
    queryFn: () => api.get<{ forecast: { label: string; count: number }[] }>("/api/dashboard/renewal-forecast"),
  });
}

// Insurance list
export function useInsuranceList(params: Record<string, string>) {
  const qs = new URLSearchParams(params).toString();
  return useQuery({
    queryKey: ["insurance", "list", params],
    queryFn: () =>
      api.get<{
        records: {
          id: number;
          address: string;
          insured: string;
          propertyType: string;
          valuer: string;
          pocFrom: string | null;
          pocTo: string | null;
          status: string;
          updatedAt: string;
        }[];
        total: number;
        page: number;
        pageSize: number;
      }>(`/api/insurance?${qs}`),
  });
}

// Single record
export function useInsuranceRecord(id: string | undefined) {
  return useQuery({
    queryKey: ["insurance", id],
    queryFn: () => api.get<{ record: Record<string, unknown> }>(`/api/insurance/${id}`),
    enabled: !!id,
  });
}

export function useCreateInsuranceRecord() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<{ record: Record<string, unknown> }>("/api/insurance", body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["insurance", "list"] });
    },
  });
}

export function useUpdateInsuranceRecord(id: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.put<{ record: Record<string, unknown> }>(`/api/insurance/${id}`, body),
    onSuccess: (data) => {
      queryClient.setQueryData(["insurance", id], data);
      queryClient.invalidateQueries({ queryKey: ["insurance", "list"] });
    },
  });
}

export function useDeleteInsuranceRecord(id: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ success: boolean }>(`/api/insurance/${id}/delete`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["insurance", "list"] });
      queryClient.invalidateQueries({ queryKey: ["insurance", id] });
    },
  });
}

// Lookups
export function usePropertyTypes() {
  return useQuery({
    queryKey: ["lookups", "property-types"],
    queryFn: () =>
      api.get<{
        items: {
          id: number;
          name: string;
          isActive: boolean;
          sortOrder: number;
          createdAt: string;
          updatedAt: string;
        }[];
      }>("/api/property-types"),
  });
}

export function useSubsoilOptions() {
  return useQuery({
    queryKey: ["lookups", "subsoil-options"],
    queryFn: () =>
      api.get<{
        items: {
          id: number;
          name: string;
          isActive: boolean;
          sortOrder: number;
          createdAt: string;
          updatedAt: string;
        }[];
      }>("/api/subsoil-options"),
  });
}

// Global settings
export function useGlobalSettings() {
  return useQuery({
    queryKey: ["settings", "global"],
    queryFn: () => api.get<{ gstEnabled: boolean }>("/api/settings/global"),
  });
}

export function useUpdateGlobalSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { gstEnabled: boolean }) => api.put<{ gstEnabled: boolean }>("/api/settings/global", body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settings", "global"] });
    },
  });
}

export function useValuers() {
  return useQuery({
    queryKey: ["lookups", "valuers"],
    queryFn: () =>
      api.get<{
        items: {
          id: number;
          name: string;
          shortCode: string;
          qualifications: string;
          email: string | null;
          isActive: boolean;
          signatureUrl: string | null;
        }[];
      }>("/api/valuers"),
  });
}
